import {
  CardKrookies,
  ConvertedKrookie,
  KrookieBackendPayload,
  KrookieStatus,
} from "../../interfaces/krookie";
import editIcon from "../../images/edit-16.svg";
import needApproveIcon from "../../images/clock.svg";
import approveIcon from "../../images/approve.svg";
import rejectIcon from "../../images/rejected.svg";
import { LandPlot } from "../../interfaces/card";
import {
  buildKrookieSerial,
  buildRevision,
  getNewChangeNumber,
} from "../../utils/krookie-helpers";
import {
  KrookiePlot,
  LogComment,
  updateConvertedKrookieDataAsync,
} from "../../services/reducers/master-plan-modification";
import { AppDispatch } from "../../services/store";
import { UserInfo } from "../../interfaces/user-info";
import { saveKrookie } from "../../services/api";

export const getStatusIcon = (status: KrookieStatus): string => {
  switch (status) {
    case KrookieStatus.Project:
      return editIcon;
    case KrookieStatus.NeedApproval:
      return needApproveIcon;
    case KrookieStatus.Approved:
      return approveIcon;
    case KrookieStatus.Rejected:
      return rejectIcon;
    default:
      return editIcon;
  }
};

export const appendKrookiesData = (
  formData: FormData,
  krookiesDataToBackEnd: KrookieBackendPayload[],
) => {
  krookiesDataToBackEnd.forEach((krookie, index) => {
    Object.entries(krookie).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val, idx) => {
          formData.append(`Krookies[${index}].${key}[${idx}]`, val);
        });
      } else {
        formData.append(`Krookies[${index}].${key}`, value);
      }
    });
  });
};

export const appendOldPlots = (
  formData: FormData,
  selectedPlots: LandPlot[],
) => {
  if (selectedPlots && selectedPlots.length > 0) {
    selectedPlots.forEach((plot, index) => {
      const oldPlots = {
        plotId: plot.objectId,
        plotNum: plot.number,
        geometry: JSON.stringify(plot.geometry),
        attributes: JSON.stringify(plot.attributes),
      };
      Object.entries(oldPlots).forEach(([key, value]) => {
        formData.append(`OldPlots[${index}].${key}`, String(value));
      });
    });
  }
};

export const prepareKrookieApproveData = async (
  currentKrookieKey: number,
  currentConvertedKrookieData: ConvertedKrookie,
  user: UserInfo | null,
  dispatch: AppDispatch,
  pdfElement: HTMLElement | null,
  generatePdf: (
    pdfContainer: HTMLElement | null,
    includeLink?: boolean,
    action?: "open" | "return",
  ) => Promise<Blob | null>,
) => {
  const { revision, changeNum, currentDate } = buildRevision(
    currentConvertedKrookieData.historyLog,
  );

  await dispatch(
    updateConvertedKrookieDataAsync({
      id: currentKrookieKey,
      data: {
        revision,
        signatureDrafter: currentDate,
        directorName: user?.userName,
        signatureDirector: currentDate,
        userPosition: user?.userPosition,
      },
    }),
  );

  const blob = await generatePdf(pdfElement, false, "return");
  if (!blob) {
    return;
  }

  const file = new File(
    [blob],
    `${currentConvertedKrookieData.plotNumber}.pdf`,
    { type: blob.type },
  );

  return {
    krookieId: currentConvertedKrookieData.id.toString(),
    changeNum,
    documentBody: file,
    revision,
    currentDate,
  };
};

export const prepareKrookieData = async (
  statusId: KrookieStatus | undefined = undefined,
  pdfElement: HTMLElement | null,
  generatePdf: (
    pdfContainer: HTMLElement | null,
    includeLink?: boolean,
    action?: "open" | "return",
  ) => Promise<Blob | null>,
  currentKrookie: CardKrookies,
  currentConvertedKrookieData: ConvertedKrookie,
  logComment: LogComment,
  currentKrookiePlot: KrookiePlot | undefined,
  currentKrookieKey: number,
  estateSerial: string | null,
): Promise<KrookieBackendPayload | null> => {
  if (!currentKrookiePlot) {
    return null;
  }

  const blob = await generatePdf(pdfElement, false, "return");
  if (blob) {
    const file = new File([blob], `${currentKrookie.plotNumber}.pdf`, {
      type: blob.type,
    });

    // Keep the same screenshot in the same slot (top/first, bottom/second)
    // from live preview through save/reload/print — no swap.
    const minScaleScreenshots = currentKrookie.screenshots.firstScreenshot;
    const maxScaleScreenshots = currentKrookie.screenshots.secondScreenshot;

    const responseFirst = await fetch(minScaleScreenshots.url);
    const blobFirst = await responseFirst.blob();
    const fileFirst = new File(
      [blobFirst],
      `${currentKrookie.plotNumber}_firstScreen.svg`,
      { type: blobFirst.type },
    );

    const responseSecond = await fetch(maxScaleScreenshots.url);
    const blobSecond = await responseSecond.blob();
    const fileSecond = new File(
      [blobSecond],
      `${currentKrookie.plotNumber}_secondScreen.svg`,
      { type: blobSecond.type },
    );

    const filteredNotes = Array.isArray(currentKrookie.notes)
      ? currentKrookie.notes.filter((note) => note.trim() !== "")
      : currentKrookie.notes;

    const changeNum = currentConvertedKrookieData?.historyLog?.length
      ? getNewChangeNumber(currentConvertedKrookieData.historyLog)
      : "001";

    return {
      serialNum: estateSerial
        ? buildKrookieSerial(estateSerial, currentKrookie.plotNumber)
        : currentKrookie.plotNumber,
      date: new Date().toISOString(),
      cityId: currentKrookie.cityEn.value,
      phaseId: currentKrookie.phase.value,
      newPlotNum: currentKrookie.plotNumber,
      streetNum: currentKrookie.streetNumber,
      landUse: currentKrookie.landUse.value,
      geometry: JSON.stringify(currentKrookiePlot.newGeometry),
      buildArea: currentKrookie.buildArea,
      area: currentKrookie.area,
      maxHeight: currentKrookie.height,
      frontSetback: currentKrookie.front,
      sideSetback: currentKrookie.side,
      backSetback: currentKrookie.back,
      previousKrookieNum: currentKrookie.previousKrookieCode,
      notes: filteredNotes,
      document: file,
      changeNum: changeNum,
      minScale: minScaleScreenshots.scale,
      maxScale: maxScaleScreenshots.scale,
      minScaleScreenshot: fileFirst,
      maxScaleScreenshot: fileSecond,
      logComment: logComment[currentKrookieKey]?.[-1] || "",
      plotStatus: currentKrookie.status.value,
      plotLocationStatusCode: currentKrookie.locationStatusCode.value,
      plotIsInvOp: currentKrookie.isInvestmentOpportunity ? 1 : 0,
      ...(currentConvertedKrookieData && {
        id: currentConvertedKrookieData.id,
      }),
      ...(typeof statusId !== "undefined" ? { statusId } : {}),
    };
  }

  return null;
};

export const processSaveResponse = async (
  formData: FormData,
  setVisibleDocumentKrookie: (visible: boolean) => void,
  setVisibleModalSuccessful?: (visible: boolean) => void,
) => {
  await saveKrookie(formData).then(() => {
    setVisibleDocumentKrookie(false);
    if (setVisibleModalSuccessful) {
      setVisibleModalSuccessful(true);
    }
  });
};

export const updateKrookiesWithLatestComments = (
  savedKrookiesData: KrookieBackendPayload[],
  allKrookieKeys: number[],
  logComment: LogComment,
): KrookieBackendPayload[] => {
  return savedKrookiesData.map((krookieData, index) => {
    if (!krookieData) return krookieData;

    const krookieKey = allKrookieKeys[index];
    const latestComment = logComment[krookieKey]?.[-1] || "";

    if (krookieData.logComment !== latestComment) {
      return {
        ...krookieData,
        logComment: latestComment,
      };
    }

    return krookieData;
  });
};
